import { useEffect, useRef, useCallback } from 'react';
import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { RichTextEditor } from '../components/RichTextEditor';
import { sanitizeTelegramHtml } from '../utils';

const API_URL = import.meta.env.VITE_API_URL;

export interface ExtraField {
  blockExtraFieldsId: number;
  blockId: number;
  title: string;
  isVisible: boolean;
  canChangeVisible: boolean;
  code: string;
  type: 'msg' | 'btn';
  group: string;
  index: number;
  isDraggable: boolean;
}

interface ExtraFieldsTabProps {
  fields: ExtraField[];
  onChange: (updated: ExtraField[]) => void;
}

const GROUP_LABELS: Record<string, string> = {
  alternative_main_menu: 'Стандартне меню під клавіатурою користувача',
  goods_with_card: 'Налаштування текстів та функціональних кнопок кошику',
  katalog_info: 'Налаштування текстів та функціональних кнопок каталогу',
  goods_btn: 'Налаштування текстів та функціональних кнопок картки продукту',
  extra_catalog:
    'Налаштування текстів та функціональних кнопок функціоналу каталогу',
  extra_cart_order_block:
    'Налаштування текстів та функціональних кнопок замовлення',
  extra_cart_order_block_set:
    'Налаштування текстів та функціональних кнопок у процесі замовлення',
  extra_cart: 'Налаштування текстів та функціональних кнопок кошику',
  extra_field_support:
    'Налаштування текстів та функціональних кнопок підтримки',
  extra_history: 'Налаштування текстів та функціональних кнопок блок історії',
  extra_catalog_control: 'Налаштування контролю над каталогом',
  extra_goods_btn: 'Налаштування контролю над продукцією у каталогу або кошику',
  extra_cart_empty: 'Налаштування контролю над кнопками у пустому кошику'
};

const FIELD_HELP: Record<string, string> = {
  alt_main_menu_reg_msg:
    'Повідомлення що надходить користувачу після реєстрації',
  alt_main_menu_btn: 'Кнопка "Головне меню"',
  alt_main_menu_katalog: 'Кнопка "Каталог"',
  alt_main_menu_cash: 'Кнопка "Кошик"',
  alt_main_menu_support: 'Кнопка "Підтримка"',
  alt_main_menu_history: 'Кнопка "Історія замовлень"',
  msg_add_to_cart_search:
    'Повідомлення, яке бачить користувач коли додав продукт до кошику',
  msg_add_to_cart:
    'Повідомлення, яке бачить користувач коли додав продукт до кошику',
  katalog_info:
    'Повідомлення, яке бачить користувач коли переходить до каталогу',
  extra_to_card: 'Кнопка "Перейти до кошика"',
  minus: 'Кнопка яка дозволяє зменшити кількість товару',
  plus: 'Кнопка яка дозволяє збільшити кількість товару',
  quanity: 'Кнопка яка відображає кількість товару',
  quanity_goods_block: 'Блок у якому відображені налаштування +, - та інші.',
  addToCart: 'Кнопка, яка дозволяє додати товар до кошику',
  extra_to_buy:
    'Кнопка, яка дозволяє "Продовжити покупки" і перейти до каталогу',
  remove_goods: 'Кнопка, яка дозволяє видалити товар із кошику',
  extra_back_to_categories_from_catalog:
    'Кнопка, яка дозволяє повернутись до категорій',
  katalog_msg_city:
    'Повідомлення у блоці зміни міста. Макрос ${city} використовується для відображення міста',
  choose_city: 'Кнопка зміни міста (тільки якщо місто вибрано)',
  change_city: 'Кнопка вибору міста (якщо місто не вибрано)',
  message_remove: 'Кнопка приховування повідомлення',
  catalog_back: 'Гортати назад у каталозі',
  catalog_next: 'Гортати вперед у каталозі',
  extra_from_remove_goods_msg: 'Повідомлення про видалення товару з кошику',
  extra_from_goods_to_main_menu: 'Повернення у головне меню',
  all_categories: 'Перехід до всіх категорій',
  back_to_catalog: 'Повернення до категорій',
  catalog_info: 'Повідомлення при відсутності опису категорії',
  go_to_catalog: 'Перехід до каталогу',
  go_to_catalog_back_to_menu: 'Перехід у головне меню з каталогу',
  cart_msg_calendar: 'Повідомлення при виборі не правильної дати у календарі',
  cart_phone_next: 'Кнопка "Далі" при оформленні замовлення',
  cart_create_order: 'Створення замовлення',
  cart_to_catalog: 'Повернення до каталогу з кошику',
  cart_phone_back: 'Повернення до головного меню',
  final_order: 'Підтвердження та фінальне оформлення замовлення',
  create_order:
    'Кнопка, що дозволяє в процесі оформлення замовлення змінити ПІБ',
  cart_back_to_main_menu: 'Повернення назад у меню',
  order_create_shop:
    'Кнопка, що дозволяє в процесі оформлення замовлення змінити магазин',
  order_change_date:
    'Кнопка, що дозволяє в процесі оформлення замовлення змінити дату',
  cart: 'Кнопка, що дозволяє почати оформлення замовлення спочатку',
  cart_is_empty_msg: 'Повідомлення, що кошик користувача пустий',
  extra_field_support:
    'Повідомлення, яке відображається після звернення користувача',
  extra_back_to_orders: 'Кнопка, що дозволяє повернутись назад до замовлень',
  order_num:
    'Відображення номеру замовлення. ${index + 1} - порядковий номер, ${order.orderId} - унікальний код замовлення, ${order.status} - статус замовлення, ${order.date} - дата замовлення, ${order.deliveryDate} - дата доставки замовлення',
  no_orders:
    'Повідомлення, що відображається користувачу у якого немає замовлень',
  no_orders_back: 'Кнопка, що відображається для повернення до головного меню',
};

const GROUP_ORDER = [
  'alternative_main_menu',
  'goods_btn',
  'extra_goods_btn',
  'goods_with_card',
  'katalog_info',
  'extra_catalog',
  'extra_cart_order_block',
  'extra_cart_order_block_set',
  'extra_cart',
  'extra_field_support',
  'extra_history',
  'extra_catalog_control',
  'extra_cart_empty'
];

function SortableBtnRow({
  field,
  onTitleChange,
  onToggleVisible,
}: {
  field: ExtraField;
  onTitleChange: (id: number, title: string) => void;
  onToggleVisible: (id: number) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: field.blockExtraFieldsId,
    disabled: !field.isDraggable,
  });

  const help = FIELD_HELP[field.code];

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : field.isVisible ? 1 : 0.4,
      }}
      display="flex"
      alignItems="center"
      gap={1}
      sx={{
        p: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
      }}
    >
      {field.isDraggable ? (
        <IconButton size="small" {...attributes} {...listeners}>
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      ) : (
        <Box sx={{ width: 32 }} />
      )}

      <Tooltip title={help || 'Немає опису'} arrow placement="top-start">
        <span style={{ width: '100%' }}>
          <TextField
            size="small"
            fullWidth
            value={field.title}
            // +++ исключение: quanity_goods_block — title нельзя менять
            disabled={field.code === 'quanity_goods_block'}
            onChange={(e) =>
              onTitleChange(field.blockExtraFieldsId, e.target.value)
            }
          />
        </span>
      </Tooltip>

      {field.canChangeVisible && (
        <IconButton
          size="small"
          onClick={() => onToggleVisible(field.blockExtraFieldsId)}
        >
          {field.isVisible ? (
            <VisibilityIcon fontSize="small" />
          ) : (
            <VisibilityOffIcon fontSize="small" />
          )}
        </IconButton>
      )}
    </Box>
  );
}

function SortableGoodsBtnCard({
  field,
  onToggleVisible,
  onTitleChange,
}: {
  field: ExtraField;
  onTitleChange: (id: number, title: string) => void;
  onToggleVisible: (id: number) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: field.blockExtraFieldsId,
    disabled: !field.isDraggable,
  });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : field.isVisible ? 1 : 0.4,
      }}
      display="inline-flex"
      alignItems="center"
      gap={0.5}
      sx={{
        p: 0.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        width: 'fit-content',
        whiteSpace: 'nowrap',
      }}
    >
      {field.isDraggable && (
        <IconButton
          size="small"
          {...attributes}
          {...listeners}
          sx={{ p: 0.25 }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      )}

      <TextField
        size="small"
        value={field.title}
        onChange={(e) =>
          onTitleChange(field.blockExtraFieldsId, e.target.value)
        }
        sx={{
          '& .MuiInputBase-input': { fontSize: 12, p: '4px 6px' },
          width: `${Math.max(field.title.length, 8)}ch`, // ширина по содержимому
        }}
      />

      {field.canChangeVisible && (
        <IconButton
          size="small"
          onClick={() => onToggleVisible(field.blockExtraFieldsId)}
          sx={{ p: 0.25 }}
        >
          {field.isVisible ? (
            <VisibilityIcon fontSize="small" />
          ) : (
            <VisibilityOffIcon fontSize="small" />
          )}
        </IconButton>
      )}
    </Box>
  );
}

type GroupsMap = Record<string, ExtraField[]>;

export function ExtraFieldsTab({ fields, onChange }: ExtraFieldsTabProps) {
  const debounceTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>(
    {},
  );
  const sensors = useSensors(useSensor(PointerSensor));
  const getHelp = (code: string) => FIELD_HELP[code];

  const saveField = useCallback(async (id: number, patch: any) => {
    const safePatch = {
      ...patch,
      ...(patch.title ? { title: sanitizeTelegramHtml(patch.title) } : {}),
    };

    await fetch(`${API_URL}/extra-fields/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(safePatch),
    });
  }, []);

  const handleTitleChange = (id: number, title: string) => {
    const safeTitle = sanitizeTelegramHtml(title);

    onChange(
      fields.map((f) =>
        f.blockExtraFieldsId === id ? { ...f, title: safeTitle } : f,
      ),
    );

    clearTimeout(debounceTimers.current[id]);
    debounceTimers.current[id] = setTimeout(() => {
      saveField(id, { title: safeTitle });
    }, 400);
  };

  const handleToggleVisible = (id: number) => {
    const field = fields.find((f) => f.blockExtraFieldsId === id);
    if (!field) return;

    const isVisible = !field.isVisible;

    onChange(
      fields.map((f) =>
        f.blockExtraFieldsId === id ? { ...f, isVisible } : f,
      ),
    );
    saveField(id, { isVisible });
  };

  const handleDragEnd = async (groupKey: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const groupItems = fields
      .filter(
        (f) =>
          f.group === groupKey &&
          f.type === 'btn' &&
          f.group !== 'extra_goods_btn',
      )
      .sort((a, b) => a.index - b.index);

    const oldIndex = groupItems.findIndex(
      (f) => f.blockExtraFieldsId === active.id,
    );
    const newIndex = groupItems.findIndex(
      (f) => f.blockExtraFieldsId === over.id,
    );

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(groupItems, oldIndex, newIndex);
    const updated = reordered.map((f, index) => ({ ...f, index }));

    const merged = fields.map((f) => {
      const found = updated.find(
        (u) => u.blockExtraFieldsId === f.blockExtraFieldsId,
      );
      return found ?? f;
    });

    onChange(merged);

    await fetch(`${API_URL}/extra-fields-reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        updated.map((f) => ({
          blockExtraFieldsId: f.blockExtraFieldsId,
          index: f.index,
        })),
      ),
    });
  };

  const handleGoodsBtnDragEnd = async (
    groupKey: string,
    event: DragEndEvent,
  ) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const groupItems = fields
      .filter((f) => f.group === groupKey && f.group === 'extra_goods_btn')
      .sort((a, b) => a.index - b.index);

    const oldIndex = groupItems.findIndex(
      (f) => f.blockExtraFieldsId === active.id,
    );
    const newIndex = groupItems.findIndex(
      (f) => f.blockExtraFieldsId === over.id,
    );

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(groupItems, oldIndex, newIndex);
    const updated = reordered.map((f, index) => ({ ...f, index }));

    const merged = fields.map((f) => {
      const found = updated.find(
        (u) => u.blockExtraFieldsId === f.blockExtraFieldsId,
      );
      return found ?? f;
    });

    onChange(merged);

    await fetch(`${API_URL}/extra-fields-reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        updated.map((f) => ({
          blockExtraFieldsId: f.blockExtraFieldsId,
          index: f.index,
        })),
      ),
    });
  };

  const groups = fields.reduce<GroupsMap>((acc, f) => {
    if (!acc[f.group]) acc[f.group] = [];
    acc[f.group].push(f);
    return acc;
  }, {});

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {GROUP_ORDER.filter((groupKey) => groups[groupKey]?.length).map(
        (groupKey) => {
          const groupFields = groups[groupKey];
          const sorted = [...groupFields].sort((a, b) => a.index - b.index);

          const msgs = sorted.filter((f) => f.type === 'msg');

          const goodsBtns = sorted.filter(
            (f) => f.type === 'btn' && f.group === 'extra_goods_btn',
          );
          const btns = sorted.filter(
            (f) => f.type === 'btn' && f.group !== 'extra_goods_btn',
          );

          return (
            <Box key={groupKey}>
              <Box sx={{ fontSize: 12, fontWeight: 600, mb: 1 }}>
                {GROUP_LABELS[groupKey] ?? groupKey}
              </Box>

              <Box display="flex" flexDirection="column" gap={1}>
                {msgs.map((f) => (
                  <Box key={f.blockExtraFieldsId}>
                    <RichTextEditor
                      value={f.title}
                      onChange={(val) =>
                        handleTitleChange(
                          f.blockExtraFieldsId,
                          sanitizeTelegramHtml(val),
                        )
                      }
                    />

                    {getHelp(f.code) && (
                      <Box
                        sx={{
                          mt: 0.5,
                          fontSize: 12,
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                        }}
                      >
                        <Box component="span">{getHelp(f.code)}</Box>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

              {goodsBtns.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => handleGoodsBtnDragEnd(groupKey, e)}
                >
                  <SortableContext
                    items={goodsBtns.map((f) => f.blockExtraFieldsId)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <Box
                      display="flex"
                      flexDirection="row"
                      gap={1}
                      mt={1}
                      sx={{ overflowX: 'auto' }}
                    >
                      {goodsBtns.map((f) => (
                        <SortableGoodsBtnCard
                          key={f.blockExtraFieldsId}
                          field={f}
                          onTitleChange={handleTitleChange}
                          onToggleVisible={handleToggleVisible}
                        />
                      ))}
                    </Box>
                  </SortableContext>
                </DndContext>
              )}

              {btns.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => handleDragEnd(groupKey, e)}
                >
                  <SortableContext
                    items={btns.map((f) => f.blockExtraFieldsId)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Box display="flex" flexDirection="column" gap={1} mt={1}>
                      {btns.map((f) => (
                        <SortableBtnRow
                          key={f.blockExtraFieldsId}
                          field={f}
                          onTitleChange={handleTitleChange}
                          onToggleVisible={handleToggleVisible}
                        />
                      ))}
                    </Box>
                  </SortableContext>
                </DndContext>
              )}
            </Box>
          );
        },
      )}
    </Box>
  );
}
